import React, { useEffect, useMemo, useState } from "react";

import {
  Card,
  Button,
  Select,
  Row,
  Col,
  Space,
  Typography,
  Spin,
  message,
  Tag,
  Empty,
  Alert,
  Tooltip,
} from "antd";

import {
  FilePdfOutlined,
  ReloadOutlined,
  DownloadOutlined,
  TrophyOutlined,
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  BarChartOutlined,
  FileProtectOutlined,
  DeleteOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

import Swal from "sweetalert2";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

import ScreenGradeSheet from "./ScreenGradeSheet";
import { pdf } from "@react-pdf/renderer";
import { ClassReportDocument } from "../../SchoolDashApp/ReportCard/ClassReportPDF";

const { Title, Text } = Typography;

/* ============================================================
   BRAND COLORS
============================================================ */

const COLORS = {
  deepNavy: "#031326",
  navy: "#061A33",
  royalNavy: "#0A2E5A",
  blue: "#124A8A",
  brightBlue: "#1E63B5",

  charcoal: "#111827",
  darkCharcoal: "#0B1220",
  slate: "#334155",

  gold: "#D4A017",
  brightGold: "#F4C542",
  amber: "#A87400",

  white: "#FFFFFF",
  cream: "#F8FAFC",
  soft: "#F1F5F9",
  softBlue: "#EAF2FB",
  softGold: "#FFF7D6",

  text: "#0F172A",
  muted: "#64748B",
  border: "#D9E2EC",
  deepBorder: "#B7C5D6",

  success: "#1F8A5B",
  danger: "#B42318",
};

/* ============================================================
   SWEETALERT BRAND CONFIG
============================================================ */

const swalConfig = {
  confirmButtonColor: COLORS.navy,
  cancelButtonColor: COLORS.charcoal,
};

/* ============================================================
   PERIOD REPORT
============================================================ */

const PeriodReport = () => {
  const {
    selectedYear: activeAcademicYear,
    loading: yearLoading,
  } = useAcademicYear();

  const [gradeClasses, setGradeClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [periods, setPeriods] = useState([]);

  const [loadingStudentDownload, setLoadingStudentDownload] =
    useState(false);

  const [selectedGradeClass, setSelectedGradeClass] =
    useState(null);

  const [selectedStudent, setSelectedStudent] =
    useState(null);

  const [selectedPeriod, setSelectedPeriod] =
    useState(null);

  const [downloadGradeClass, setDownloadGradeClass] =
    useState(null);

  const [downloadPeriod, setDownloadPeriod] =
    useState(null);

  const [gradeSheet, setGradeSheet] = useState(null);

  const [loading, setLoading] = useState(false);

  const [loadingClassDownload, setLoadingClassDownload] =
    useState(false);

  /* ============================================================
     LOAD GRADE CLASSES
  ============================================================ */

  useEffect(() => {
    api
      .get("/api/users/grade-classes/")
      .then((res) => {
        setGradeClasses(res.data || []);
      })
      .catch(() => {
        setGradeClasses([]);
      });
  }, []);

  /* ============================================================
     LOAD PERIODS
  ============================================================ */

  useEffect(() => {
    api
      .get("/api/users/periods/")
      .then((res) => {
        setPeriods(res.data || []);
      })
      .catch(() => {
        setPeriods([]);
      });
  }, []);

  /* ============================================================
     LOAD STUDENTS
  ============================================================ */

  useEffect(() => {
    if (selectedGradeClass && activeAcademicYear) {
      api
        .get(
          `/students-by-grade-class/?grade_class=${selectedGradeClass.id}&academic_year=${activeAcademicYear.id}`
        )
        .then((res) => {
          setStudents(res.data || []);
        })
        .catch(() => {
          setStudents([]);
        });

      setSelectedStudent(null);
      setGradeSheet(null);
    } else {
      setStudents([]);
    }
  }, [selectedGradeClass, activeAcademicYear]);

  /* ============================================================
     DERIVED DATA
  ============================================================ */

  const studentCount = useMemo(
    () => students.length,
    [students]
  );

  const activeYearLabel =
    activeAcademicYear?.name ||
    activeAcademicYear?.year ||
    "Academic Year";

  /* ============================================================
     GENERATE STUDENT GRADE SHEET
  ============================================================ */

  const handleGenerate = async () => {
    if (
      !selectedGradeClass ||
      !selectedStudent ||
      !selectedPeriod
    ) {
      message.error(
        "Please select grade class, student and period"
      );

      return;
    }

    if (!activeAcademicYear) {
      message.error("Academic year not loaded");
      return;
    }

    try {
      setLoading(true);

      const response = await api.post(
        "/api/users/student-averages/",
        {
          grade_class: selectedGradeClass.id,
          student: selectedStudent.id,
          period: selectedPeriod.id,
          academic_year: activeAcademicYear.id,
        }
      );

      setGradeSheet(response.data);

      message.success("Grade sheet generated");
    } catch (error) {
      message.error(
        error.response?.data?.error ||
          "Failed to generate grade sheet"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     DOWNLOAD SINGLE STUDENT GRADE SHEET
  ============================================================ */

  const handleDownloadStudentGradeSheet = async () => {
    if (!gradeSheet) {
      Swal.fire({
        icon: "warning",
        title: "Grade Sheet Required",
        text: "Please generate a student grade sheet first.",
        confirmButtonText: "OK",
        ...swalConfig,
      });

      return;
    }

    if (!selectedGradeClass || !selectedPeriod) {
      Swal.fire({
        icon: "warning",
        title: "Selection Required",
        text: "Please select a grade class and period.",
        confirmButtonText: "OK",
        ...swalConfig,
      });

      return;
    }

    try {
      setLoadingStudentDownload(true);

      Swal.fire({
        title: "Preparing Grade Sheet",
        text:
          "Please wait while the student grade sheet is being prepared...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const blob = await pdf(
        <ClassReportDocument
          reports={[gradeSheet]}
          gradeClass={selectedGradeClass}
          period={selectedPeriod}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `${
        gradeSheet.student_name ||
        selectedStudent?.full_name ||
        "Student"
      }_Grade_Sheet_${selectedPeriod.name}.pdf`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Download Complete",
        text: `The grade sheet for ${
          gradeSheet.student_name ||
          selectedStudent?.full_name
        } has been downloaded successfully.`,
        confirmButtonText: "OK",
        ...swalConfig,
      });
    } catch (error) {
      console.error(
        "Student grade sheet download error:",
        error
      );

      Swal.close();

      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text:
          error.response?.data?.error ||
          "The student grade sheet could not be generated or downloaded. Please try again.",
        confirmButtonText: "OK",
        ...swalConfig,
      });
    } finally {
      setLoadingStudentDownload(false);
    }
  };

  /* ============================================================
     DOWNLOAD CLASS GRADES
  ============================================================ */

  const handleDownloadClassGrades = async () => {
    if (!downloadGradeClass || !downloadPeriod) {
      Swal.fire({
        icon: "warning",
        title: "Selection Required",
        text:
          "Please select a grade class and period before downloading.",
        confirmButtonText: "OK",
        ...swalConfig,
      });

      return;
    }

    if (!activeAcademicYear) {
      Swal.fire({
        icon: "error",
        title: "Academic Year Unavailable",
        text:
          "The active academic year has not loaded yet.",
        confirmButtonText: "OK",
        ...swalConfig,
      });

      return;
    }

    try {
      setLoadingClassDownload(true);

      Swal.fire({
        title: "Preparing Grade Reports",
        text:
          "Please wait while the class grade reports are being generated...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.post(
        "/api/users/student-averages/class-report/",
        {
          grade_class: downloadGradeClass.id,
          period: downloadPeriod.id,
          academic_year: activeAcademicYear.id,
        }
      );

      if (
        !response.data ||
        response.data.length === 0
      ) {
        Swal.close();

        Swal.fire({
          icon: "info",
          title: "No Reports Found",
          text:
            "There are no grade reports available for the selected class and period.",
          confirmButtonText: "OK",
          ...swalConfig,
        });

        return;
      }

      const blob = await pdf(
        <ClassReportDocument
          reports={response.data}
          gradeClass={downloadGradeClass}
          period={downloadPeriod}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `Class_${downloadGradeClass.name}_${downloadPeriod.name}.pdf`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Download Complete",
        text: `The grade reports for ${downloadGradeClass.name} - ${downloadPeriod.name} have been downloaded successfully.`,
        confirmButtonText: "OK",
        ...swalConfig,
      });
    } catch (error) {
      console.error(
        "Class grade download error:",
        error
      );

      Swal.close();

      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text:
          error.response?.data?.error ||
          "The class grade reports could not be generated or downloaded. Please try again.",
        confirmButtonText: "OK",
        ...swalConfig,
      });
    } finally {
      setLoadingClassDownload(false);
    }
  };

  /* ============================================================
     DELETE GRADE SHEET
  ============================================================ */

  const handleDelete = () => {
    if (!gradeSheet) return;

    Swal.fire({
      title: "Delete Grade Sheet?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: COLORS.danger,
      cancelButtonColor: COLORS.charcoal,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(
            `/api/users/student-averages/${gradeSheet.id}/`
          );

          setGradeSheet(null);

          Swal.fire({
            title: "Deleted",
            text: "Grade sheet removed successfully.",
            icon: "success",
            confirmButtonText: "OK",
            ...swalConfig,
          });
        } catch {
          Swal.fire({
            title: "Error",
            text: "Delete failed.",
            icon: "error",
            confirmButtonText: "OK",
            ...swalConfig,
          });
        }
      }
    });
  };

  /* ============================================================
     SELECT STYLES
  ============================================================ */

  const selectStyle = {
    width: "100%",
  };

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <>
      <style>
        {`
          .period-report-page {
            min-height: 100%;
            padding: 8px 0 35px;
            background:
              radial-gradient(
                circle at 5% 10%,
                rgba(30, 99, 181, .06),
                transparent 28%
              ),
              radial-gradient(
                circle at 95% 30%,
                rgba(212, 160, 23, .07),
                transparent 25%
              ),
              linear-gradient(
                180deg,
                #f8fafc 0%,
                #eef3f8 100%
              );
          }

          .period-report-hero {
            position: relative;
            overflow: hidden;
            border: 0 !important;
            border-radius: 24px !important;
            background:
              linear-gradient(
                135deg,
                #031326 0%,
                #061a33 48%,
                #0a2e5a 100%
              ) !important;
            box-shadow:
              0 22px 50px rgba(3, 19, 38, .22);
          }

          .period-report-hero::before {
            content: "";
            position: absolute;
            width: 260px;
            height: 260px;
            right: -100px;
            top: -120px;
            border-radius: 50%;
            background: rgba(212, 160, 23, .14);
          }

          .period-report-hero::after {
            content: "";
            position: absolute;
            width: 190px;
            height: 190px;
            left: -90px;
            bottom: -110px;
            border-radius: 50%;
            border: 1px solid rgba(255,255,255,.10);
          }

          .period-report-hero-inner {
            position: relative;
            z-index: 2;
            padding: 28px 30px;
          }

          .period-report-kicker {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 6px 12px;
            border-radius: 999px;
            background: rgba(255,255,255,.08);
            border: 1px solid rgba(255,255,255,.12);
            color: #f4c542;
            font-size: 11px;
            font-weight: 800;
            letter-spacing: 1.5px;
            text-transform: uppercase;
          }

          .period-report-title {
            margin: 15px 0 7px !important;
            color: #ffffff !important;
            font-weight: 800 !important;
            letter-spacing: -.7px;
          }

          .period-report-subtitle {
            color: rgba(255,255,255,.72);
            max-width: 680px;
            font-size: 14px;
            line-height: 1.7;
          }

          .period-report-year {
            display: inline-flex;
            align-items: center;
            gap: 7px;
            margin-top: 16px;
            padding: 8px 13px;
            border-radius: 10px;
            color: #ffffff;
            background: rgba(255,255,255,.08);
            border: 1px solid rgba(255,255,255,.14);
            font-size: 13px;
          }

          .period-report-hero-icon {
            width: 90px;
            height: 90px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: auto;
            border-radius: 26px;
            color: #f4c542;
            background:
              linear-gradient(
                145deg,
                rgba(244,197,66,.2),
                rgba(255,255,255,.06)
              );
            border: 1px solid rgba(244,197,66,.28);
          }

          /*
             STATIC GOLD ACCENT LINE
             No moving/shimmer animation.
          */
          .period-report-gold-line {
            height: 4px;
            width: 100%;
            background:
              linear-gradient(
                90deg,
                #d4a017,
                #f4c542,
                #d4a017
              );
          }

          .period-report-section {
            margin-top: 20px;
            border-radius: 20px !important;
            border: 1px solid #d9e2ec !important;
            box-shadow: 0 10px 28px rgba(15, 23, 42, .06);
          }

          .period-report-section .ant-card-body {
            padding: 24px;
          }

          .period-report-section-heading {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
          }

          .period-report-section-icon {
            width: 42px;
            height: 42px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 13px;
            color: #f4c542;
            background:
              linear-gradient(
                145deg,
                #031326,
                #0a2e5a
              );
            box-shadow: 0 8px 18px rgba(3, 19, 38, .16);
          }

          .period-report-section-title {
            margin: 0 !important;
            color: #0f172a !important;
            font-size: 18px !important;
            font-weight: 800 !important;
          }

          .period-report-section-caption {
            color: #64748b;
            font-size: 12px;
          }

          .period-report-stat {
            position: relative;
            overflow: hidden;
            height: 100%;
            border-radius: 18px !important;
            border: 1px solid #d9e2ec !important;
            background: #ffffff !important;
            box-shadow: 0 8px 24px rgba(15, 23, 42, .06);
            transition:
              transform .25s ease,
              box-shadow .25s ease;
          }

          .period-report-stat:hover {
            transform: translateY(-4px);
            box-shadow: 0 16px 32px rgba(15, 23, 42, .10);
          }

          .period-report-stat::before {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            height: 4px;
            background: linear-gradient(
              90deg,
              #031326,
              #d4a017,
              #f4c542
            );
          }

          .period-report-stat-body {
            padding: 19px;
          }

          .period-report-stat-icon {
            width: 42px;
            height: 42px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 12px;
            margin-bottom: 13px;
            color: #d4a017;
            background: #fff7d6;
          }

          .period-report-stat-value {
            color: #061a33;
            font-size: 23px;
            font-weight: 800;
          }

          .period-report-stat-label {
            color: #64748b;
            font-size: 12px;
            margin-top: 3px;
          }

          .period-report-control {
            border-radius: 14px !important;
          }

          .period-report-control .ant-select-selector {
            min-height: 46px !important;
            display: flex !important;
            align-items: center !important;
            border-radius: 12px !important;
            border-color: #cbd5e1 !important;
            box-shadow: none !important;
          }

          .period-report-control.ant-select-focused
            .ant-select-selector {
            border-color: #d4a017 !important;
            box-shadow:
              0 0 0 3px rgba(212,160,23,.10) !important;
          }

          .period-report-primary {
            height: 46px !important;
            border: 0 !important;
            border-radius: 12px !important;
            padding: 0 20px !important;
            font-weight: 700 !important;
            color: #ffffff !important;
            background:
              linear-gradient(
                135deg,
                #031326,
                #0a2e5a
              ) !important;
            box-shadow:
              0 9px 20px rgba(3,19,38,.18);
          }

          .period-report-primary:hover {
            color: #ffffff !important;
            transform: translateY(-1px);
            box-shadow:
              0 13px 25px rgba(3,19,38,.24);
          }

          .period-report-gold-button {
            height: 46px !important;
            border: 0 !important;
            border-radius: 12px !important;
            padding: 0 20px !important;
            font-weight: 800 !important;
            color: #031326 !important;
            background:
              linear-gradient(
                135deg,
                #d4a017,
                #f4c542
              ) !important;
            box-shadow:
              0 9px 20px rgba(212,160,23,.20);
          }

          .period-report-gold-button:hover {
            color: #031326 !important;
            transform: translateY(-1px);
          }

          .period-report-danger-button {
            height: 44px !important;
            border-radius: 11px !important;
            font-weight: 700 !important;
          }

          .period-report-preview {
            margin-top: 22px;
            border-radius: 18px;
            border: 1px solid #d9e2ec;
            overflow: hidden;
            background: #ffffff;
          }

          .period-report-preview-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 15px;
            padding: 15px 18px;
            background:
              linear-gradient(
                90deg,
                #031326,
                #061a33,
                #0a2e5a
              );
            color: #ffffff;
          }

          .period-report-preview-label {
            display: flex;
            align-items: center;
            gap: 9px;
            font-size: 13px;
            font-weight: 800;
          }

          .period-report-preview-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }

          .period-report-info-strip {
            display: flex;
            flex-wrap: wrap;
            gap: 9px;
            padding: 14px 18px;
            background: #f8fafc;
            border-bottom: 1px solid #e2e8f0;
          }

          .period-report-info-chip {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 10px;
            border-radius: 8px;
            color: #334155;
            background: #ffffff;
            border: 1px solid #d9e2ec;
            font-size: 11px;
            font-weight: 700;
          }

          .period-report-empty {
            padding: 30px 10px;
          }

          .period-report-download-panel {
            position: relative;
            overflow: hidden;
            margin-top: 24px;
            border-radius: 20px;
            padding: 24px;
            color: #ffffff;
            background:
              linear-gradient(
                135deg,
                #031326 0%,
                #061a33 55%,
                #111827 100%
              );
            box-shadow:
              0 15px 35px rgba(3,19,38,.16);
          }

          .period-report-download-panel::after {
            content: "";
            position: absolute;
            width: 180px;
            height: 180px;
            right: -70px;
            top: -80px;
            border-radius: 50%;
            border: 1px solid rgba(244,197,66,.18);
          }

          .period-report-download-panel-inner {
            position: relative;
            z-index: 2;
          }

          .period-report-download-title {
            margin: 0 0 5px !important;
            color: #ffffff !important;
            font-weight: 800 !important;
          }

          .period-report-download-description {
            color: rgba(255,255,255,.65);
            font-size: 12px;
            line-height: 1.6;
          }

          .period-report-download-icon {
            width: 52px;
            height: 52px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 15px;
            color: #f4c542;
            background: rgba(244,197,66,.12);
            border: 1px solid rgba(244,197,66,.25);
          }

          .period-report-spinner {
            min-height: 180px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .period-report-loading-text {
            margin-top: 10px;
            color: #64748b;
            font-size: 12px;
          }

          @media (max-width: 767px) {
            .period-report-hero-inner {
              padding: 22px 20px;
            }

            .period-report-hero-icon {
              display: none;
            }

            .period-report-section .ant-card-body {
              padding: 17px;
            }

            .period-report-preview-header {
              align-items: flex-start;
              flex-direction: column;
            }

            .period-report-download-panel {
              padding: 20px;
            }
          }
        `}
      </style>

      <div className="period-report-page">
        <Card
          className="period-report-hero"
          bodyStyle={{ padding: 0 }}
        >
          <div className="period-report-hero-inner">
            <Row
              gutter={[20, 20]}
              align="middle"
            >
              <Col xs={24} md={18}>
                <div className="period-report-kicker">
                  <SafetyCertificateOutlined />
                  Academic Assessment
                </div>

                <Title
                  className="period-report-title"
                  level={1}
                >
                  Period Grade Reports
                </Title>

                <div className="period-report-subtitle">
                  Generate, review and securely download
                  individual student grade sheets or complete
                  class grade reports for the active academic
                  year.
                </div>

                <div className="period-report-year">
                  <CalendarOutlined />
                  <span>
                    Active Academic Year:
                    <strong style={{ marginLeft: 5 }}>
                      {yearLoading
                        ? "Loading..."
                        : activeYearLabel}
                    </strong>
                  </span>
                </div>
              </Col>

              <Col xs={24} md={6}>
                <div className="period-report-hero-icon">
                  <BarChartOutlined
                    style={{ fontSize: 42 }}
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div className="period-report-gold-line" />
        </Card>

        <Row
          gutter={[16, 16]}
          style={{ marginTop: 20 }}
        >
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="period-report-stat"
              bodyStyle={{ padding: 0 }}
            >
              <div className="period-report-stat-body">
                <div className="period-report-stat-icon">
                  <TeamOutlined />
                </div>

                <div className="period-report-stat-value">
                  {gradeClasses.length}
                </div>

                <div className="period-report-stat-label">
                  Available Grade Classes
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              className="period-report-stat"
              bodyStyle={{ padding: 0 }}
            >
              <div className="period-report-stat-body">
                <div className="period-report-stat-icon">
                  <UserOutlined />
                </div>

                <div className="period-report-stat-value">
                  {studentCount}
                </div>

                <div className="period-report-stat-label">
                  Students in Selected Class
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              className="period-report-stat"
              bodyStyle={{ padding: 0 }}
            >
              <div className="period-report-stat-body">
                <div className="period-report-stat-icon">
                  <CalendarOutlined />
                </div>

                <div className="period-report-stat-value">
                  {periods.length}
                </div>

                <div className="period-report-stat-label">
                  Available Periods
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              className="period-report-stat"
              bodyStyle={{ padding: 0 }}
            >
              <div className="period-report-stat-body">
                <div className="period-report-stat-icon">
                  <FileProtectOutlined />
                </div>

                <div className="period-report-stat-value">
                  {gradeSheet ? "Ready" : "—"}
                </div>

                <div className="period-report-stat-label">
                  Current Grade Sheet
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        <Card
          className="period-report-section"
          bodyStyle={{ padding: 24 }}
        >
          <div className="period-report-section-heading">
            <div className="period-report-section-icon">
              <UserOutlined />
            </div>

            <div>
              <Title
                className="period-report-section-title"
                level={4}
              >
                Generate Student Grade Sheet
              </Title>

              <Text className="period-report-section-caption">
                Select a class, student and period to generate
                an individual academic report.
              </Text>
            </div>
          </div>

          {yearLoading ? (
            <div className="period-report-spinner">
              <div style={{ textAlign: "center" }}>
                <Spin size="large" />

                <div className="period-report-loading-text">
                  Loading active academic year...
                </div>
              </div>
            </div>
          ) : (
            <>
              <Row gutter={[16, 18]}>
                <Col xs={24} md={8}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 7,
                      color: COLORS.text,
                    }}
                  >
                    Grade Class
                  </Text>

                  <Select
                    className="period-report-control"
                    style={selectStyle}
                    placeholder="Select Grade Class"
                    value={selectedGradeClass?.id}
                    showSearch
                    optionFilterProp="label"
                    options={gradeClasses.map((item) => ({
                      label: item.name,
                      value: item.id,
                      data: item,
                    }))}
                    onChange={(value, option) => {
                      setSelectedGradeClass(
                        option?.data || null
                      );
                    }}
                  />
                </Col>

                <Col xs={24} md={8}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 7,
                      color: COLORS.text,
                    }}
                  >
                    Student
                  </Text>

                  <Select
                    className="period-report-control"
                    style={selectStyle}
                    placeholder={
                      selectedGradeClass
                        ? "Select Student"
                        : "Select Grade Class First"
                    }
                    disabled={!selectedGradeClass}
                    value={selectedStudent?.id}
                    showSearch
                    optionFilterProp="label"
                    options={students.map((item) => ({
                      label: item.full_name,
                      value: item.id,
                      data: item,
                    }))}
                    onChange={(value, option) => {
                      setSelectedStudent(
                        option?.data || null
                      );
                    }}
                  />
                </Col>

                <Col xs={24} md={8}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 7,
                      color: COLORS.text,
                    }}
                  >
                    Period
                  </Text>

                  <Select
                    className="period-report-control"
                    style={selectStyle}
                    placeholder="Select Period"
                    value={selectedPeriod?.id}
                    showSearch
                    optionFilterProp="label"
                    options={periods.map((item) => ({
                      label: item.name,
                      value: item.id,
                      data: item,
                    }))}
                    onChange={(value, option) => {
                      setSelectedPeriod(
                        option?.data || null
                      );
                    }}
                  />
                </Col>
              </Row>

              <div style={{ marginTop: 20 }}>
                <Space wrap>
                  <Button
                    className="period-report-primary"
                    icon={<BarChartOutlined />}
                    loading={loading}
                    onClick={handleGenerate}
                  >
                    Generate Grade Sheet
                  </Button>

                  {selectedGradeClass && (
                    <Button
                      className="period-report-danger-button"
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        setSelectedStudent(null);
                        setSelectedPeriod(null);
                        setGradeSheet(null);
                      }}
                    >
                      Clear Selection
                    </Button>
                  )}
                </Space>
              </div>

              {gradeSheet ? (
                <div className="period-report-preview">
                  <div className="period-report-preview-header">
                    <div className="period-report-preview-label">
                      <TrophyOutlined
                        style={{ color: COLORS.brightGold }}
                      />

                      Grade Sheet Ready
                    </div>

                    <div className="period-report-preview-actions">
                      <Tooltip title="Download this student's grade sheet">
                        <Button
                          className="period-report-gold-button"
                          icon={<DownloadOutlined />}
                          loading={
                            loadingStudentDownload
                          }
                          onClick={
                            handleDownloadStudentGradeSheet
                          }
                        >
                          Download PDF
                        </Button>
                      </Tooltip>

                      <Tooltip title="Delete generated grade sheet">
                        {/* <Button
                          danger
                          ghost
                          icon={<DeleteOutlined />}
                          onClick={handleDelete}
                        >
                          Delete
                        </Button> */}
                      </Tooltip>
                    </div>
                  </div>

                  <div className="period-report-info-strip">
                    <div className="period-report-info-chip">
                      <UserOutlined />
                      {gradeSheet.student_name ||
                        selectedStudent?.full_name ||
                        "Student"}
                    </div>

                    <div className="period-report-info-chip">
                      <TeamOutlined />
                      {selectedGradeClass?.name ||
                        "Grade Class"}
                    </div>

                    <div className="period-report-info-chip">
                      <CalendarOutlined />
                      {selectedPeriod?.name ||
                        "Period"}
                    </div>

                    <div className="period-report-info-chip">
                      <SafetyCertificateOutlined />
                      {activeYearLabel}
                    </div>
                  </div>

                  <div style={{ padding: "20px" }}>
                    <ScreenGradeSheet
                      data={gradeSheet}
                    />
                  </div>
                </div>
              ) : (
                <div className="period-report-empty">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span style={{ color: COLORS.muted }}>
                        Select the required information and
                        generate a grade sheet to preview it
                        here.
                      </span>
                    }
                  />
                </div>
              )}
            </>
          )}
        </Card>

        <Card
          className="period-report-section"
          bodyStyle={{ padding: 24 }}
        >
          <div className="period-report-section-heading">
            <div className="period-report-section-icon">
              <TeamOutlined />
            </div>

            <div>
              <Title
                className="period-report-section-title"
                level={4}
              >
                Download Class Grades
              </Title>

              <Text className="period-report-section-caption">
                Generate a complete PDF report containing the
                grade reports for every student in a class.
              </Text>
            </div>
          </div>

          <Alert
            showIcon
            icon={<FilePdfOutlined />}
            message="Class Report Generator"
            description="Choose the grade class and period. The system will generate the available student grade reports into one PDF document."
            style={{
              marginBottom: 22,
              borderRadius: 13,
              borderColor: "#d9e2ec",
              background: "#f8fafc",
            }}
          />

          <Row gutter={[16, 18]}>
            <Col xs={24} md={12}>
              <Text
                strong
                style={{
                  display: "block",
                  marginBottom: 7,
                  color: COLORS.text,
                }}
              >
                Grade Class
              </Text>

              <Select
                className="period-report-control"
                style={selectStyle}
                placeholder="Select Grade Class"
                value={downloadGradeClass?.id}
                showSearch
                optionFilterProp="label"
                options={gradeClasses.map((item) => ({
                  label: item.name,
                  value: item.id,
                  data: item,
                }))}
                onChange={(value, option) => {
                  setDownloadGradeClass(
                    option?.data || null
                  );
                }}
              />
            </Col>

            <Col xs={24} md={12}>
              <Text
                strong
                style={{
                  display: "block",
                  marginBottom: 7,
                  color: COLORS.text,
                }}
              >
                Period
              </Text>

              <Select
                className="period-report-control"
                style={selectStyle}
                placeholder="Select Period"
                value={downloadPeriod?.id}
                showSearch
                optionFilterProp="label"
                options={periods.map((item) => ({
                  label: item.name,
                  value: item.id,
                  data: item,
                }))}
                onChange={(value, option) => {
                  setDownloadPeriod(
                    option?.data || null
                  );
                }}
              />
            </Col>
          </Row>

          <div className="period-report-download-panel">
            <div className="period-report-download-panel-inner">
              <Row
                gutter={[18, 18]}
                align="middle"
              >
                <Col flex="auto">
                  <Space
                    align="start"
                    size={14}
                  >
                    <div className="period-report-download-icon">
                      <FilePdfOutlined
                        style={{ fontSize: 25 }}
                      />
                    </div>

                    <div>
                      <Title
                        className="period-report-download-title"
                        level={4}
                      >
                        Class Grade Report PDF
                      </Title>

                      <div className="period-report-download-description">
                        {downloadGradeClass
                          ? `${downloadGradeClass.name}`
                          : "No grade class selected"}

                        {" • "}

                        {downloadPeriod
                          ? downloadPeriod.name
                          : "No period selected"}

                        {" • "}

                        {activeYearLabel}
                      </div>
                    </div>
                  </Space>
                </Col>

                <Col>
                  <Button
                    className="period-report-gold-button"
                    icon={<DownloadOutlined />}
                    loading={loadingClassDownload}
                    onClick={
                      handleDownloadClassGrades
                    }
                  >
                    Download Class Grades
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        </Card>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 20,
          }}
        >
          <Tag
            icon={<SafetyCertificateOutlined />}
            style={{
              border: "1px solid #d9e2ec",
              borderRadius: 999,
              padding: "6px 12px",
              color: COLORS.navy,
              background: "#ffffff",
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            Academic Records • Secure PDF Reporting
          </Tag>
        </div>
      </div>
    </>
  );
};

export default PeriodReport;

